import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { GetClassPricingRulesResponse } from '@classpass/protobuf-definitions/lib/web/models/partners/class_pricing_rules';
import { CLASS_ENTITY_TYPE } from 'constants/logic';
import URLS from 'constants/urls';
import { httpClient } from 'helpers/http';
import { transformKeysToCamelCase, transformKeysToSnakeCase, transformV2ClientResponse } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { useIsServicePricingProofEnabled } from 'hooks/useIsServicePricingProofEnabled';
import { selectActiveVenue, selectRouteParams, selectUserInfo } from 'selectors/general';
import { fetchRetailRateRequestsByVenue, mergeRetailRateRequestsByClassId } from '../../retailRateRequests.helpers';

const PAGE_SIZE = 100;

async function fetchServices(venueId, { page = 1, pageSize = PAGE_SIZE }) {
  const response = await httpClient.get(URLS.manageServices.services.list(venueId, { page, pageSize }));
  const { services, pagination } = transformV2ClientResponse(response);

  const hasMore = pagination?.totalPages > page;

  return { services, hasMore };
}

async function fetchClasstivityClasses(venueId) {
  const params = transformKeysToSnakeCase({
    onlyActive: false,
    entityType: CLASS_ENTITY_TYPE.APPOINTMENT,
    includeClassPricing: false,
    includeDisabled: true,
  });

  const response = await httpClient.get(URLS.venue.classes(venueId), { params });
  return transformV2ClientResponse(response.data);
}

async function fetchPricingRules(venueId) {
  const response = await httpClient.get(URLS.venue.classPricing(venueId));
  return transformKeysToCamelCase(GetClassPricingRulesResponse.fromJSON(response.data))?.pricingRules;
}

export function mapServicesWithPrice(services = [], classtivityClasses = [], pricingRules = []) {
  const mappedServices = services.map(service => {
    const classtivityClass = classtivityClasses.find(class_ => class_.externalClassId === service.openApiId);
    const classId = classtivityClass?.classId;
    const pricingRule = pricingRules.find(rule => rule.classId === classId);

    const price = pricingRule?.classPriceFormatted ? Number.parseFloat(pricingRule.classPriceFormatted) : null;

    return {
      ...service,
      classId,
      disabled: classtivityClass?.disabled ?? false,
      classPricingRule: pricingRule ?? null,
      price,
    };
  });

  return mappedServices;
}

export async function mergeManualServicesWithVenueRetailRateRequests(mappedServices, venueId, isPricingProofEnabled) {
  if (isPricingProofEnabled !== true) {
    return mappedServices;
  }

  try {
    const requests = await fetchRetailRateRequestsByVenue(venueId);

    return mergeRetailRateRequestsByClassId(mappedServices, requests);
  } catch (error) {
    reportError('Failed to fetch venue retail rate requests for manual list', { error, venueId });
    return mappedServices;
  }
}

function sortServicesAlphabetically(services) {
  return services.sort((a, b) => a.name.localeCompare(b.name));
}

const SERVICES_REFRESH_EVENT_TARGET = new EventTarget();
const SERVICES_REFRESH_EVENT = 'servicesRefresh';

export function refreshServicesContext() {
  SERVICES_REFRESH_EVENT_TARGET.dispatchEvent(new CustomEvent(SERVICES_REFRESH_EVENT));
}

const ServicesContext = createContext({});

export function ServicesContextProvider({ children }) {
  const routeParams = useSelector(selectRouteParams);
  const { itemId } = routeParams;

  const { isMsInventoryVenue, venueId } = useSelector(selectActiveVenue);
  const userInfo = useSelector(selectUserInfo);
  const isPricingProofEnabled = useIsServicePricingProofEnabled();

  const [classtivityClasses, setClasstivityClasses] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [page, setPage] = useState(1);

  const [servicesWithPrice, setServicesWithPrice] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  function selectService(serviceId) {
    const service = servicesWithPrice.find(({ id }) => id === serviceId);
    setSelectedService(service);
  }

  const fetchAllAndSetup = useCallback(async () => {
    setIsLoading(true);

    const [{ services, hasMore }, classtivityClasses_, pricingRules_] = await Promise.all([
      fetchServices(venueId, { page: 1, pageSize: PAGE_SIZE }),
      fetchClasstivityClasses(venueId),
      fetchPricingRules(venueId),
    ]).catch(error => {
      reportError(error);
      return [{ services: [], hasMore: false }, [], []];
    });

    let mappedServices = sortServicesAlphabetically(mapServicesWithPrice(services, classtivityClasses_, pricingRules_));

    mappedServices = await mergeManualServicesWithVenueRetailRateRequests(
      mappedServices,
      venueId,
      isPricingProofEnabled
    );

    setClasstivityClasses(classtivityClasses_);
    setPricingRules(pricingRules_);
    setPage(hasMore ? 2 : 1);

    setServicesWithPrice(mappedServices);
    setSelectedService(mappedServices[0] || null);
    setIsLoading(false);
  }, [isPricingProofEnabled, venueId]);

  useEffect(() => {
    if (isMsInventoryVenue && venueId && userInfo?.userId) {
      fetchAllAndSetup();
    }
  }, [fetchAllAndSetup, isMsInventoryVenue, userInfo?.userId, venueId]);

  useEffect(() => {
    const handleRefresh = () => {
      if (isMsInventoryVenue && venueId && userInfo?.userId) {
        fetchAllAndSetup();
      }
    };

    SERVICES_REFRESH_EVENT_TARGET.addEventListener(SERVICES_REFRESH_EVENT, handleRefresh);

    return () => {
      SERVICES_REFRESH_EVENT_TARGET.removeEventListener(SERVICES_REFRESH_EVENT, handleRefresh);
    };
  }, [fetchAllAndSetup, isMsInventoryVenue, userInfo?.userId, venueId]);

  useEffect(() => {
    async function effect() {
      if (isMsInventoryVenue && page !== 1 && !isLoading) {
        const { services, hasMore } = await fetchServices(venueId, { page, pageSize: PAGE_SIZE });
        let mappedServices = mapServicesWithPrice(services, classtivityClasses, pricingRules);

        mappedServices = await mergeManualServicesWithVenueRetailRateRequests(
          mappedServices,
          venueId,
          isPricingProofEnabled
        );

        if (hasMore) {
          setPage(prev => prev + 1);
        }

        setServicesWithPrice(prev => sortServicesAlphabetically([...prev, ...mappedServices]));
      }
    }

    effect();
  }, [classtivityClasses, isLoading, isMsInventoryVenue, isPricingProofEnabled, page, pricingRules, venueId]);

  useEffect(() => {
    if (isMsInventoryVenue && itemId && servicesWithPrice.length > 0) {
      selectService(itemId);
    }
  }, [isMsInventoryVenue, itemId, servicesWithPrice]);

  return (
    <ServicesContext.Provider
      value={{
        servicesWithPrice,
        selectedService,
        isLoading,
        selectService,
        refreshServices: fetchAllAndSetup,
      }}>
      {children}
    </ServicesContext.Provider>
  );
}

ServicesContextProvider.propTypes = {
  children: PropTypes.oneOf([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
};

export default ServicesContext;
